import { all, call, put, select, takeLatest } from "typed-redux-saga";
import { alertControls, loaderControls } from "react-better-html";

import { type RootState } from "../store";
import { organizationsSlice } from "../slices/organizations";
import { workspacesSlice } from "../slices/workspaces";
import { propertiesSlice } from "../slices/properties";

import { api } from "../../utils/api";
import { handleError } from "../../utils/functions";
import { LocalStorage } from "../../utils/localStorage";
import { i18n } from "../../utils/i18n";

function* onGetAllOrganizations(action: ReturnType<typeof organizationsSlice.actions.getAllOrganizations>) {
   yield* call(() => loaderControls.startLoading("organization:getAllOrganizations"));

   try {
      const response = yield* call(() => api("getAllOrganizations"));

      yield* put(organizationsSlice.actions.setOrganizations(response.data));

      const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);
      if (!selectedOrganizationId) {
         if (response.data.length > 0) {
            yield* put(
               organizationsSlice.actions.setSelectedOrganizationId({
                  organizationId: response.data[0].id,
               }),
            );
         }
      }

      action.payload?.onSuccess?.(response.data);
   } catch (error) {
      action.payload?.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("organization:getAllOrganizations"));
   }
}

function* onGetOrganizationById(action: ReturnType<typeof organizationsSlice.actions.getOrganizationById>) {
   yield* call(() => loaderControls.startLoading("organization:getOrganizationById"));
   yield* call(() => loaderControls.startLoading(`organization:getOrganizationById${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("getOrganizationById", {
            path: [action.payload.id],
         }),
      );

      yield* put(organizationsSlice.actions.updateOrganization(response.data));

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("organization:getOrganizationById"));
      yield* call(() => loaderControls.stopLoading(`organization:getOrganizationById${action.payload.id}`));
   }
}

function* onSetOrganizations(action: ReturnType<typeof organizationsSlice.actions.setOrganizations>) {
   yield* call(() =>
      action.payload ? LocalStorage.setItem("organizations", action.payload) : LocalStorage.removeItem("organizations"),
   );
}

function* onCreateOrganization(action: ReturnType<typeof organizationsSlice.actions.createOrganization>) {
   yield* call(() => loaderControls.startLoading("organization:createOrganization"));

   try {
      const response = yield* call(() =>
         api("createOrganization", {
            body: action.payload.data,
         }),
      );

      yield* put(organizationsSlice.actions.updateOrganization(response.data));
      yield* put(
         organizationsSlice.actions.setSelectedOrganizationId({
            organizationId: response.data.id,
            isChange: true,
         }),
      );

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.organizationCreated),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("organization:createOrganization"));
   }
}

function* onEditOrganization(action: ReturnType<typeof organizationsSlice.actions.editOrganization>) {
   yield* call(() => loaderControls.startLoading("organization:editOrganization"));
   yield* call(() => loaderControls.startLoading(`organization:editOrganization${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("editOrganization", {
            body: action.payload.data,
            path: [action.payload.id],
         }),
      );

      yield* put(organizationsSlice.actions.updateOrganization(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.organizationEdited),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("organization:editOrganization"));
      yield* call(() => loaderControls.stopLoading(`organization:editOrganization${action.payload.id}`));
   }
}

function* onUpdateOrganization() {
   const organizationsList = yield* select((state: RootState) => state.organizations.list);

   yield* call(() =>
      organizationsList
         ? LocalStorage.setItem("organizations", organizationsList)
         : LocalStorage.removeItem("organizations"),
   );
}

function* onSetSelectedOrganizationId(action: ReturnType<typeof organizationsSlice.actions.setSelectedOrganizationId>) {
   if (action.payload.isChange) {
      yield* put(propertiesSlice.actions.setProperties(undefined));
      yield* put(workspacesSlice.actions.setWorkspaces(undefined));
      yield* put(workspacesSlice.actions.setArchivedWorkspaces(undefined));
   }

   yield* call(() =>
      action.payload
         ? LocalStorage.setItem("selectedOrganizationId", action.payload.organizationId)
         : LocalStorage.removeItem("selectedOrganizationId"),
   );
}

export default function* organizationsSaga() {
   yield* all([
      takeLatest(organizationsSlice.actions.getAllOrganizations, onGetAllOrganizations),
      takeLatest(organizationsSlice.actions.getOrganizationById, onGetOrganizationById),
      takeLatest(organizationsSlice.actions.setOrganizations, onSetOrganizations),
      takeLatest(organizationsSlice.actions.createOrganization, onCreateOrganization),
      takeLatest(organizationsSlice.actions.editOrganization, onEditOrganization),
      takeLatest(organizationsSlice.actions.updateOrganization, onUpdateOrganization),
      takeLatest(organizationsSlice.actions.setSelectedOrganizationId, onSetSelectedOrganizationId),
   ]);
}
